import React, { useState, useEffect } from 'react';
import Icon from '../components/Icon';
import { youthAPI, aiAPI } from '../services/api';

function getUser() {
  try { return JSON.parse(localStorage.getItem('user')); } catch { return null; }
}

// ===== SVG Donut Chart =====
function DonutChart({ data, size = 160, thickness = 28, center }) {
  const r = (size - thickness) / 2;
  const circ = 2 * Math.PI * r;
  const total = data.reduce((s, d) => s + d.value, 0);
  let offset = 0;
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={thickness} />
        {data.filter(d => d.value > 0).map((d, i) => {
          const pct = d.value / (total || 1);
          const dash = pct * circ;
          const gap = total > 1 ? 3 : 0;
          const el = <circle key={i} cx={size/2} cy={size/2} r={r} fill="none" stroke={d.color} strokeWidth={thickness - 2}
            strokeDasharray={`${Math.max(0, dash - gap)} ${circ - Math.max(0, dash - gap)}`} strokeDashoffset={-offset}
            strokeLinecap="round" style={{ transition: 'all 1s ease' }} />;
          offset += dash;
          return el;
        })}
      </svg>
      {center && <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>{center}</div>}
    </div>
  );
}

// ===== SVG Bar Chart (horizontal) =====
function HBar({ label, value, max, color, icon }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'flex', alignItems: 'center', gap: 5 }}>
          {icon && <span style={{ fontSize: 13 }}>{icon}</span>}{label}
        </span>
        <span style={{ fontSize: 13, fontWeight: 800, color }}>{value}</span>
      </div>
      <div style={{ height: 10, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden' }}>
        <div style={{ height: '100%', borderRadius: 5, width: `${pct}%`, background: `linear-gradient(90deg, ${color}99, ${color})`, transition: 'width 1s ease' }} />
      </div>
    </div>
  );
}

// ===== Gauge =====
function Gauge({ value, max, label, color, size = 120 }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  const r = 48, circ = Math.PI * r;
  return (
    <div style={{ textAlign: 'center' }}>
      <svg width={size} height={70} viewBox="0 0 120 70">
        <path d="M 12 62 A 48 48 0 0 1 108 62" fill="none" stroke="#f1f5f9" strokeWidth="10" strokeLinecap="round" />
        <path d="M 12 62 A 48 48 0 0 1 108 62" fill="none" stroke={color} strokeWidth="10" strokeLinecap="round"
          strokeDasharray={`${circ}`} strokeDashoffset={circ - (circ * Math.min(pct, 100) / 100)}
          style={{ transition: 'stroke-dashoffset 1.2s ease' }} />
        <text x="60" y="52" textAnchor="middle" style={{ fontSize: 22, fontWeight: 900, fill: color }}>{pct}%</text>
      </svg>
      <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginTop: -4 }}>{label}</div>
    </div>
  );
}

// ===== Card =====
function Card({ children, style = {} }) {
  return <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 24px', ...style }}>{children}</div>;
}
function CardTitle({ icon, title, badge }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{title}</span>
      {badge && <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, background: '#f5f3ff', color: '#7c3aed' }}>{badge}</span>}
    </div>
  );
}

// ===== Mini stat =====
function MiniStat({ icon, value, label, color, bg }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderRadius: 14, background: bg || '#f8fafc', border: '1px solid #f1f5f9', flex: 1, minWidth: 140 }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>{icon}</div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 900, color: color || '#1e293b', lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: 10, fontWeight: 500, color: '#94a3b8', marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

// ===== MOCK DATA for MFY =====
const RISK_FACTORS = [
  { name: 'Ishsizlik xavfi', icon: 'briefcase', value: 78, trend: 'up', detail: "Ta'lim va bandlik asosida" },
  { name: 'Migratsiya xavfi', icon: 'plane', value: 62, trend: 'down', detail: "Qaytish ehtimoli va oilaviy holat" },
  { name: 'Huquqbuzarlik xavfi', icon: 'shield', value: 34, trend: 'stable', detail: "Nazorat va jazo tarixi" },
  { name: 'Ijtimoiy ojizlik', icon: 'heart', value: 55, trend: 'up', detail: "Kam ta'minlanganlik" },
  { name: "Ta'limdan chetlashish", icon: 'graduation', value: 41, trend: 'down', detail: "Akademik ko'rsatkichlar" },
  { name: "Sog'liq xavfi", icon: 'heart', value: 28, trend: 'stable', detail: "Nogironlik va kasalliklar" },
];

// ===== MAIN PAGE =====
export default function AIModulePage() {
  const user = getUser();
  const isAdmin = user?.role === 'viloyat_admin' || user?.role === 'tuman_admin';
  const [d, setD] = useState({});
  const [youthList, setYouthList] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const promises = [
      aiAPI.stats().then(r => setD(r.data)),
      youthAPI.list().then(r => setYouthList(r.data.slice(0, 20))),
    ];
    Promise.all(promises).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 100 }}>
      <div style={{ display: 'inline-block', width: 40, height: 40, border: '4px solid #e0e7ff', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <p style={{ color: '#7c3aed', fontWeight: 600, marginTop: 12 }}>🧠 AI tahlil yuklanmoqda...</p>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return (
    <div>
      {/* ===== HERO ===== */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 35%, #4c1d95 70%, #7c3aed 100%)',
        borderRadius: 24, padding: '28px 32px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -60, right: -40, width: 220, height: 220, borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 70%)', animation: 'pulse 4s ease-in-out infinite' }} />
        <div style={{ position: 'absolute', bottom: -40, left: '30%', width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.2) 0%, transparent 70%)' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
            <div style={{ width: 52, height: 52, borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(139,92,246,0.3)', border: '1.5px solid rgba(139,92,246,0.4)' }}>
              <span style={{ fontSize: 26 }}>🧠</span>
            </div>
            <div>
              <h2 style={{ fontSize: 21, fontWeight: 900, color: '#fff', margin: 0 }}>Sun'iy intellekt moduli</h2>
              <p style={{ fontSize: 12, color: '#c4b5fd', margin: 0 }}>AI yordamida chuqur tahlil, prognoz va avtomatik tavsiyalar</p>
            </div>
            <div style={{ marginLeft: 'auto', padding: '5px 14px', borderRadius: 20, background: 'rgba(139,92,246,0.25)', border: '1px solid rgba(139,92,246,0.4)' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: '#c4b5fd', letterSpacing: '.06em' }}>BETA</span>
            </div>
          </div>
          {/* Hero stats */}
          <div className="ai-hero-stats" style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 8 }}>
            {[
              { v: d.total || 0, l: 'Jami yoshlar', e: '👥' },
              { v: d.problems_total || 0, l: 'Muammolar', e: '📋' },
              { v: d.events_total || 0, l: 'Tadbirlar', e: '🎯' },
              { v: d.events_participants || 0, l: 'Ishtiroklar', e: '🤝' },
              { v: d.questions_total || 0, l: 'Savollar', e: '💬' },
              { v: d.xavf_yuqori || 0, l: 'Yuqori xavf', e: '⚠️' },
            ].map(s => (
              <div key={s.l} style={{ padding: '10px', borderRadius: 12, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.08)', textAlign: 'center' }}>
                <div style={{ fontSize: 16 }}>{s.e}</div>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#fff', lineHeight: 1.2 }}>{s.v}</div>
                <div style={{ fontSize: 9, color: '#a5b4fc', fontWeight: 500 }}>{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {isAdmin ? <AdminDashboard d={d} user={user} /> : <MFYDashboard factors={RISK_FACTORS} youthList={youthList} d={d} />}

      <style>{`
        @keyframes pulse { 0%,100% { transform: scale(1); opacity: 0.3; } 50% { transform: scale(1.1); opacity: 0.5; } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .ai-fade { animation: fadeUp 0.6s ease-out both; }
        .ai-hero-stats { grid-template-columns: repeat(6, 1fr); }
        @media (max-width: 900px) { .ai-hero-stats { grid-template-columns: repeat(3, 1fr); } }
        @media (max-width: 480px) { .ai-hero-stats { grid-template-columns: repeat(2, 1fr); } }
      `}</style>
    </div>
  );
}

// =====================================================
// ADMIN DASHBOARD — with charts
// =====================================================
function AdminDashboard({ d, user }) {
  const bd = d.tuman_breakdown || d.mfy_breakdown || [];
  const regionKey = user?.role === 'viloyat_admin' ? 'tuman' : 'mfy';
  const regionLabel = user?.role === 'viloyat_admin' ? 'Tuman' : 'MFY';

  const empData = [
    { label: 'Ishlamoqda', value: d.ishlamoqda || 0, color: '#22c55e' },
    { label: 'Ishsiz', value: d.ishsiz || 0, color: '#ef4444' },
    { label: "O'qimoqda", value: d.talaba || 0, color: '#3b82f6' },
    { label: 'Tadbirkor', value: d.tadbirkor || 0, color: '#f59e0b' },
    { label: 'Boshqa', value: Math.max(0, (d.total||0) - (d.ishlamoqda||0) - (d.ishsiz||0) - (d.talaba||0) - (d.tadbirkor||0)), color: '#94a3b8' },
  ].filter(x => x.value > 0);

  const riskData = [
    { label: 'Yuqori', value: d.xavf_yuqori || 0, color: '#ef4444' },
    { label: "O'rta", value: d.xavf_orta || 0, color: '#f59e0b' },
    { label: 'Past', value: d.xavf_past || 0, color: '#22c55e' },
  ];

  const ageData = [
    { label: '14-17', value: d.age_14_17 || 0, color: '#a855f7' },
    { label: '18-22', value: d.age_18_22 || 0, color: '#6366f1' },
    { label: '23-26', value: d.age_23_26 || 0, color: '#3b82f6' },
    { label: '27-30', value: d.age_27_30 || 0, color: '#0ea5e9' },
  ];

  const genderData = [
    { label: 'Erkak', value: d.male || 0, color: '#3b82f6' },
    { label: 'Ayol', value: d.female || 0, color: '#ec4899' },
  ];

  const probCats = Object.entries(d.prob_categories || {}).sort((a, b) => b[1] - a[1]);
  const catColors = { ishsizlik: '#ef4444', talim: '#3b82f6', uy_joy: '#f59e0b', moliyaviy: '#8b5cf6', oilaviy: '#ec4899', sogliq: '#10b981', huquqiy: '#0891b2', ijtimoiy: '#14b8a6', boshqa: '#64748b', infratuzilma: '#ea580c' };
  const catLabels = { ishsizlik: 'Ishsizlik', talim: "Ta'lim", uy_joy: 'Uy-joy', moliyaviy: 'Moliyaviy', oilaviy: 'Oilaviy', sogliq: "Sog'liq", huquqiy: 'Huquqiy', ijtimoiy: 'Ijtimoiy', boshqa: 'Boshqa', infratuzilma: 'Infratuzilma' };

  // Risk score computation
  const riskScore = d.total > 0 ? Math.round(((d.xavf_yuqori || 0) * 3 + (d.xavf_orta || 0) * 2 + (d.xavf_past || 0)) / (d.total * 3) * 100) : 0;
  const solveRate = d.problems_total > 0 ? Math.round((d.problems_solved || 0) / d.problems_total * 100) : 0;
  const answerRate = d.questions_total > 0 ? Math.round((d.questions_answered || 0) / d.questions_total * 100) : 0;
  const empRate = d.total > 0 ? Math.round((d.ishlamoqda || 0) / d.total * 100) : 0;

  return (
    <div className="ai-fade">
      {/* ROW 1: Gauges */}
      <div className="ai-g4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 20 }}>
        <Card style={{ textAlign: 'center', padding: '16px' }}>
          <Gauge value={100 - riskScore} max={100} label="Xavfsizlik indeksi" color={riskScore > 40 ? '#ef4444' : riskScore > 25 ? '#f59e0b' : '#22c55e'} />
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>AI risk baholash</div>
        </Card>
        <Card style={{ textAlign: 'center', padding: '16px' }}>
          <Gauge value={empRate} max={100} label="Bandlik darajasi" color={empRate > 50 ? '#22c55e' : empRate > 30 ? '#f59e0b' : '#ef4444'} />
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{d.ishlamoqda}/{d.total} band</div>
        </Card>
        <Card style={{ textAlign: 'center', padding: '16px' }}>
          <Gauge value={solveRate} max={100} label="Muammo hal qilish" color={solveRate > 60 ? '#22c55e' : solveRate > 30 ? '#f59e0b' : '#ef4444'} />
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{d.problems_solved}/{d.problems_total} hal</div>
        </Card>
        <Card style={{ textAlign: 'center', padding: '16px' }}>
          <Gauge value={answerRate} max={100} label="Savollarga javob" color={answerRate > 60 ? '#22c55e' : answerRate > 30 ? '#f59e0b' : '#ef4444'} />
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>{d.questions_answered}/{d.questions_total} javob</div>
        </Card>
      </div>

      {/* ROW 2: Donut charts */}
      <div className="ai-g3" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Employment donut */}
        <Card>
          <CardTitle icon="💼" title="Bandlik tarkibi" badge="AI tahlil" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <DonutChart data={empData} size={130} thickness={22}
              center={<><div style={{ fontSize: 22, fontWeight: 900, color: '#1e293b' }}>{d.total}</div><div style={{ fontSize: 9, color: '#94a3b8' }}>jami</div></>} />
            <div style={{ flex: 1 }}>
              {empData.map(e => (
                <div key={e.label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: e.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: '#64748b', flex: 1 }}>{e.label}</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: e.color }}>{e.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Risk donut */}
        <Card>
          <CardTitle icon="⚠️" title="Xavf darajasi" badge="AI risk" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <DonutChart data={riskData} size={130} thickness={22}
              center={<><div style={{ fontSize: 20, fontWeight: 900, color: riskScore > 40 ? '#ef4444' : '#f59e0b' }}>{riskScore}%</div><div style={{ fontSize: 9, color: '#94a3b8' }}>risk</div></>} />
            <div style={{ flex: 1 }}>
              {riskData.map(r => (
                <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 0' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: r.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: '#64748b', flex: 1 }}>{r.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 800, color: r.color }}>{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Gender + Age */}
        <Card>
          <CardTitle icon="👥" title="Jins va yosh" badge="Demografiya" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <DonutChart data={genderData} size={100} thickness={18}
              center={<div style={{ fontSize: 10, fontWeight: 700, color: '#64748b' }}>{d.male}:{d.female}</div>} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', marginBottom: 8 }}>Yosh guruhlari:</div>
              {ageData.map(a => (
                <div key={a.label} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                  <span style={{ width: 8, height: 8, borderRadius: 3, background: a.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, color: '#64748b', flex: 1 }}>{a.label} yosh</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: a.color }}>{a.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* ROW 3: Bar charts + problem categories */}
      <div className="ai-g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        {/* Social risks */}
        <Card>
          <CardTitle icon="🔍" title="Ijtimoiy xavf omillari" badge="AI aniqlash" />
          <HBar label="Ishsizlar" value={d.ishsiz||0} max={d.total||1} color="#ef4444" icon="💼" />
          <HBar label="Migrantlar" value={d.migrant||0} max={d.total||1} color="#f59e0b" icon="✈️" />
          <HBar label="Profilaktik hisobda" value={d.profilaktik||0} max={d.total||1} color="#dc2626" icon="🔒" />
          <HBar label="Kam ta'minlangan" value={d.kam_taminlangan||0} max={d.total||1} color="#ca8a04" icon="🏠" />
          <HBar label="Muammosi bor" value={d.problems_open||0} max={d.total||1} color="#8b5cf6" icon="📋" />
          <HBar label="Muddati o'tgan" value={d.problems_overdue||0} max={d.problems_total||1} color="#b91c1c" icon="⏰" />
        </Card>

        {/* Problem categories */}
        <Card>
          <CardTitle icon="🏷️" title="Muammo toifalari" badge="AI toifalash" />
          {probCats.length > 0 ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 14 }}>
                <DonutChart
                  data={probCats.map(([k, v]) => ({ label: catLabels[k] || k, value: v, color: catColors[k] || '#94a3b8' }))}
                  size={120} thickness={20}
                  center={<><div style={{ fontSize: 18, fontWeight: 900 }}>{d.problems_total}</div><div style={{ fontSize: 8, color: '#94a3b8' }}>muammo</div></>}
                />
              </div>
              {probCats.map(([k, v]) => (
                <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: catColors[k] || '#94a3b8', flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: '#475569', flex: 1 }}>{catLabels[k] || k}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: catColors[k] || '#475569' }}>{v}</span>
                  <span style={{ fontSize: 10, color: '#94a3b8' }}>({d.problems_total > 0 ? Math.round(v / d.problems_total * 100) : 0}%)</span>
                </div>
              ))}
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: 30, color: '#94a3b8', fontSize: 13 }}>Ma'lumot yo'q</div>
          )}
        </Card>
      </div>

      {/* ROW 4: Cross-module stats */}
      <div className="ai-g4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        <MiniStat icon="📋" value={d.problems_total||0} label="Jami muammolar" color="#8b5cf6" />
        <MiniStat icon="⏰" value={d.problems_overdue||0} label="Muddati o'tgan" color="#ef4444" />
        <MiniStat icon="🎯" value={d.events_done||0} label="O'tkazilgan tadbirlar" color="#22c55e" />
        <MiniStat icon="💬" value={d.questions_waiting||0} label="Javobsiz savollar" color="#f59e0b" />
      </div>

      {/* ROW 5: Regional breakdown table */}
      {bd.length > 0 && (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>🏢</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{regionLabel}lar bo'yicha qiyoslash</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>{bd.length} ta {regionLabel.toLowerCase()}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
              <thead>
                <tr style={{ background: '#fafbff' }}>
                  {['#', regionLabel, 'Jami', '👨 Erkak', '👩 Ayol', '💼 Ishsiz', '📚 Talaba', '✅ Band', '✈️ Migrant', '🔒 Profil.', '⚠️ Yuqori', '🟡 O\'rta', '🟢 Past', '📋 Muammo'].map((h, i) => (
                    <th key={i} style={{ padding: '11px 8px', fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.3px', borderBottom: '2px solid #e2e8f0', textAlign: i < 2 ? 'left' : 'center', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bd.map((row, i) => {
                  const name = row[regionKey] || "Noma'lum";
                  const rPct = row.jami > 0 ? Math.round(row.xavf_yuqori / row.jami * 100) : 0;
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background .15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#fafbff'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}>
                      <td style={{ padding: '10px 8px', fontSize: 11, color: '#94a3b8' }}>{i + 1}</td>
                      <td style={{ padding: '10px 8px' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#1e293b' }}>{name}</div>
                        {rPct > 20 && <div style={{ fontSize: 9, color: '#ef4444', fontWeight: 600 }}>⚠️ Risk {rPct}%</div>}
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: 800, color: '#1e40af', fontSize: 13 }}>{row.jami}</td>
                      <TdBadge v={row.erkak} bg="#dbeafe" c="#2563eb" />
                      <TdBadge v={row.ayol} bg="#fce7f3" c="#db2777" />
                      <TdBadge v={row.ishsiz} bg="#fef2f2" c="#dc2626" />
                      <TdBadge v={row.talaba} bg="#dbeafe" c="#2563eb" />
                      <TdBadge v={row.ishlamoqda} bg="#f0fdf4" c="#16a34a" />
                      <TdBadge v={row.migrant} bg="#fffbeb" c="#ca8a04" />
                      <TdBadge v={row.profilaktik} bg="#fef2f2" c="#dc2626" />
                      <td style={{ textAlign: 'center' }}>{row.xavf_yuqori > 0 ? <span style={{ color: '#ef4444', fontWeight: 800 }}>{row.xavf_yuqori}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.xavf_orta > 0 ? <span style={{ color: '#f59e0b', fontWeight: 700 }}>{row.xavf_orta}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <td style={{ textAlign: 'center' }}>{row.xavf_past > 0 ? <span style={{ color: '#22c55e', fontWeight: 700 }}>{row.xavf_past}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                      <TdBadge v={row.muammo} bg="#fdf4ff" c="#a855f7" />
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: '#f8fafc', borderTop: '2px solid #e2e8f0' }}>
                  <td style={{ padding: '12px 8px' }} />
                  <td style={{ padding: '12px 8px', fontWeight: 700, color: '#1e293b', fontSize: 12 }}>JAMI</td>
                  {['jami','erkak','ayol','ishsiz','talaba','ishlamoqda','migrant','profilaktik','xavf_yuqori','xavf_orta','xavf_past','muammo'].map(k => {
                    const s = bd.reduce((a, r) => a + (r[k]||0), 0);
                    const colors = { jami: '#1e40af', erkak: '#2563eb', ayol: '#db2777', ishsiz: '#dc2626', talaba: '#2563eb', ishlamoqda: '#16a34a', migrant: '#ca8a04', profilaktik: '#dc2626', xavf_yuqori: '#ef4444', xavf_orta: '#f59e0b', xavf_past: '#22c55e', muammo: '#a855f7' };
                    return <td key={k} style={{ textAlign: 'center', fontWeight: 800, color: colors[k] || '#475569', fontSize: 12 }}>{s}</td>;
                  })}
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* ROW 6: AI Insights */}
      <div style={{ marginTop: 20, background: 'linear-gradient(135deg, #faf5ff, #f0f9ff)', borderRadius: 16, border: '1.5px solid #e0e7ff', padding: '20px 24px' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#4338ca', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 18 }}>💡</span> AI xulosalari va tavsiyalar
        </div>
        <div className="ai-g3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          <InsightCard
            type={empRate > 40 ? 'success' : 'warning'}
            title={empRate > 40 ? 'Bandlik ijobiy' : 'Bandlik past'}
            desc={`Yoshlarning ${empRate}% ishlamoqda. ${d.ishsiz || 0} nafar ishsiz yosh uchun bandlik dasturlari talab qilinadi.`}
          />
          <InsightCard
            type={d.problems_overdue > 0 ? 'danger' : 'success'}
            title={d.problems_overdue > 0 ? `${d.problems_overdue} muammo kechikkan` : 'Muammolar nazoratda'}
            desc={d.problems_overdue > 0 ? "Kechikkan muammolarni zudlik bilan hal qilish zarur. AI ularni ustuvor qilib belgilaydi." : "Barcha muammolar belgilangan muddatda ko'rilmoqda."}
          />
          <InsightCard
            type={d.questions_waiting > 3 ? 'warning' : 'success'}
            title={d.questions_waiting > 0 ? `${d.questions_waiting} savol javobsiz` : 'Barcha savollar javoblangan'}
            desc={d.questions_waiting > 0 ? "Javobsiz savollar hokimlik reytingiga ta'sir qiladi. Tezroq javob berish tavsiya etiladi." : "Ajoyib! Barcha savollar o'z vaqtida javoblangan."}
          />
        </div>
      </div>

      <style>{`
        .ai-g4 { grid-template-columns: repeat(4, 1fr); }
        .ai-g3 { grid-template-columns: repeat(3, 1fr); }
        .ai-g2 { grid-template-columns: 1fr 1fr; }
        @media (max-width: 1100px) { .ai-g4 { grid-template-columns: repeat(2, 1fr); } .ai-g3 { grid-template-columns: 1fr; } }
        @media (max-width: 768px) { .ai-g2 { grid-template-columns: 1fr; } .ai-g4 { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}

function TdBadge({ v, bg, c }) {
  return (
    <td style={{ textAlign: 'center' }}>
      {v > 0 ? <span style={{ padding: '2px 7px', borderRadius: 7, fontSize: 11, fontWeight: 600, background: bg, color: c }}>{v}</span> : <span style={{ color: '#cbd5e1' }}>—</span>}
    </td>
  );
}

function InsightCard({ type, title, desc }) {
  const styles = {
    success: { bg: '#f0fdf4', border: '#bbf7d0', icon: '✅', titleColor: '#166534' },
    warning: { bg: '#fffbeb', border: '#fde68a', icon: '⚠️', titleColor: '#92400e' },
    danger: { bg: '#fef2f2', border: '#fecaca', icon: '🔴', titleColor: '#991b1b' },
  };
  const s = styles[type] || styles.warning;
  return (
    <div style={{ padding: '14px 16px', borderRadius: 12, background: s.bg, border: `1px solid ${s.border}` }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: s.titleColor, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
        {s.icon} {title}
      </div>
      <div style={{ fontSize: 11, color: '#78716c', lineHeight: 1.6 }}>{desc}</div>
    </div>
  );
}

// =====================================================
// MFY DASHBOARD
// =====================================================
function MFYDashboard({ factors, youthList, d }) {
  const [selectedYouth, setSelectedYouth] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const simulateAnalysis = (yid) => {
    setAnalyzing(true);
    setSelectedYouth(null);
    setTimeout(() => {
      const y = youthList.find(x => x.id === parseInt(yid));
      if (y) setSelectedYouth(y);
      setAnalyzing(false);
    }, 1800);
  };

  return (
    <div className="ai-fade">
      {/* Risk overview grid */}
      <div className="mfy-risk" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
        {factors.map(f => {
          const color = f.value > 70 ? '#ef4444' : f.value > 50 ? '#f59e0b' : f.value > 30 ? '#eab308' : '#22c55e';
          return (
            <div key={f.name} style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: 0, right: 0, width: 80, height: 80, borderRadius: '0 0 0 80px', background: color + '08' }} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: color + '12', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon name={f.icon} size={18} color={color} />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b' }}>{f.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8' }}>{f.detail}</div>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, height: 10, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: 5, width: `${f.value}%`, background: `linear-gradient(90deg, ${color}88, ${color})`, transition: 'width 1s ease' }} />
                </div>
                <span style={{ fontSize: 18, fontWeight: 900, color, minWidth: 40, textAlign: 'right' }}>{f.value}%</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 11, fontWeight: 600, color: f.trend === 'up' ? '#ef4444' : f.trend === 'down' ? '#22c55e' : '#64748b' }}>
                {f.trend === 'up' ? '📈 Oshmoqda' : f.trend === 'down' ? '📉 Kamaymoqda' : '➡️ Barqaror'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Individual risk scanner */}
      <Card>
        <CardTitle icon="🔍" title="Individual risk skaner" />
        <select onChange={e => e.target.value && simulateAnalysis(e.target.value)}
          style={{ width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #e2e8f0', fontSize: 14, fontFamily: 'inherit', marginBottom: 16 }}>
          <option value="">Yoshni tanlang va AI tahlil qiling...</option>
          {youthList.map(y => <option key={y.id} value={y.id}>{y.full_name} ({y.age} yosh)</option>)}
        </select>

        {analyzing && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ display: 'inline-block', width: 48, height: 48, border: '4px solid #e0e7ff', borderTopColor: '#7c3aed', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <p style={{ fontSize: 14, fontWeight: 600, color: '#7c3aed', marginTop: 12 }}>🧠 AI tahlil qilmoqda...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {selectedYouth && !analyzing && (
          <div style={{ padding: 20, borderRadius: 14, background: 'linear-gradient(135deg, #faf5ff, #f5f3ff)', border: '1.5px solid #e9d5ff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: 'linear-gradient(135deg, #7c3aed, #6366f1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700 }}>
                {selectedYouth.full_name.split(' ').map(w => w[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700 }}>{selectedYouth.full_name}</div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>{selectedYouth.age} yosh • {selectedYouth.gender === 'male' ? 'Erkak' : 'Ayol'}</div>
              </div>
              <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                <div style={{ fontSize: 28, fontWeight: 900, color: selectedYouth.prev_risk_level === 'yuqori' ? '#ef4444' : selectedYouth.prev_risk_level === "o'rta" ? '#f59e0b' : '#22c55e' }}>
                  {selectedYouth.prev_risk_level === 'yuqori' ? '78%' : selectedYouth.prev_risk_level === "o'rta" ? '45%' : '22%'}
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>Umumiy risk bali</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {[
                { l: 'Ishsizlik', v: selectedYouth.emp_status === 'ishsiz' ? 85 : 15, i: '💼' },
                { l: 'Migratsiya', v: Math.floor(Math.random() * 40 + 20), i: '✈️' },
                { l: 'Huquqiy', v: Math.floor(Math.random() * 30 + 5), i: '⚖️' },
                { l: 'Ijtimoiy', v: Math.floor(Math.random() * 50 + 15), i: '🏠' },
              ].map(r => (
                <div key={r.l} style={{ textAlign: 'center', padding: 10, borderRadius: 10, background: 'rgba(255,255,255,0.7)' }}>
                  <div style={{ fontSize: 18 }}>{r.i}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: r.v > 60 ? '#ef4444' : r.v > 40 ? '#f59e0b' : '#22c55e' }}>{r.v}%</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.l}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      <style>{`
        .mfy-risk { grid-template-columns: repeat(3, 1fr); }
        @media (max-width: 900px) { .mfy-risk { grid-template-columns: repeat(2, 1fr); } }
        @media (max-width: 480px) { .mfy-risk { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
}
